/**
 * Admin PWA install + safe update lifecycle.
 * Switches tenant vs Dev manifest by route so /dev* installs a second app.
 *
 * Install + update use CookieConsentBanner layout (bottom Card, title, body,
 * full-width action row). Chrome is portaled to document.body at --z-banner
 * (not --z-sticky): sticky table columns also use --z-sticky and mount later
 * in #root, so a sticky-band banner was painted *under* list rows.
 * --z-banner stays below --z-toast so top-right toasts never sit under TEST.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_PWA_EVENTS } from 'pi-kiosk-shared/analyticsEvents';
import { emitAdminPwaAnalytics } from './emitAdminPwaAnalytics.js';
import { isAdminCriticalFlowActive } from './adminCriticalFlowGate.js';
import {
  clearAdminInstallOfferDismissed,
  isAdminInstallOfferDue,
  markAdminInstallOfferDismissed,
  markAdminInstallOfferShown,
} from './adminInstallOfferCooldown.js';
import {
  markAdminPwaInstallEvidence,
  stampAdminPwaInstallEvidenceIfStandalone,
} from './adminPwaInstallEvidence.js';
import {
  peekAdminPwaInstallPrompt,
  setAdminPwaInstallPrompt,
  subscribeAdminPwaInstallPrompt,
  takeAdminPwaInstallPrompt,
  type AdminBeforeInstallPromptEvent,
} from './adminPwaInstallPromptStore.js';
import {
  resolveAdminPwaInstallSurface,
  type AdminPwaInstallSurface,
} from './adminPwaSurfaces.js';
import { OfflineBanner } from '../../shared/ui/OfflineBanner.js';
import { Button, Card } from '../../shared/ui/surfacePrimitives.js';
import { AdminTestEconomyChrome } from './AdminTestEconomyChrome.js';
import {
  resumeDirtyLeaveAfterNavigation,
  suspendDirtyLeaveForNavigation,
} from '../../shared/ui/dirtyLeaveHistoryPatch.js';
import { PwaRefreshBlockingOverlay } from './PwaRefreshBlockingOverlay.js';
import {
  listenAdminPwaReloadChannel,
  registerAdminPwaServiceWorker,
} from './registerAdminPwaServiceWorker.js';
import { syncAdminPwaDocumentChrome } from './syncAdminPwaDocumentChrome.js';
import {
  ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME,
  ADMIN_PWA_LIFECYCLE_ACTIONS_CLASSNAME,
  ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME,
  ADMIN_PWA_LIFECYCLE_CHROME_CLASSNAME,
  adminPwaLifecycleChromeStyle,
} from './adminPwaLifecycleChrome.js';
import { getViteEnvString } from '../../shared/vite/readViteMetaEnv.js';
import {
  ADMIN_PWA_REFRESH_ESCALATION_STEPS,
  buildAdminPwaCacheBustUrl,
  runLiveAdminPwaRefreshEscalationStep,
  stripAdminPwaRecoveryQueryFromUrl,
  type AdminPwaRefreshEscalationStep,
} from './adminPwaRefreshEscalation.js';
import { startAdminDeployVersionGatePoll } from './adminDeployVersionGate.js';

/**
 * Must exceed `CONTROLLING_RELOAD_FALLBACK_MS` in registerAdminPwaServiceWorker (4s).
 * When skipWaiting/reload never unloads the document, auto-escalate (clear SW →
 * cache-bust → path-preserving replace) instead of surfacing a user-visible failure.
 */
export const ADMIN_PWA_SAFE_REFRESH_WATCHDOG_MS = 6_000;

/** Full ladder passes before terminal recovery affordance on the overlay. */
export const ADMIN_PWA_MAX_LADDER_CYCLES = 3;

function readPwaForceUpdateEnabled(): boolean {
  const raw = getViteEnvString('VITE_PWA_FORCE_UPDATE');
  return raw === '1' || raw === 'true';
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS/i.test(ua);
}

function readIosGuideEligible(
  installSurface: AdminPwaInstallSurface = resolveAdminPwaInstallSurface(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  ),
): boolean {
  if (typeof window === 'undefined' || !isIosSafari()) {
    return false;
  }
  return isAdminInstallOfferDue(Date.now(), window.localStorage, installSurface);
}

export function AdminPwaLifecycle(): JSX.Element | null {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const surface = resolveAdminPwaInstallSurface(location.pathname);
  const surfaceRef = useRef<AdminPwaInstallSurface>(surface);
  /** Mirror of `adminPwaInstallPromptStore` — store is sole BIP owner. */
  const [deferredPrompt, setDeferredPrompt] =
    useState<AdminBeforeInstallPromptEvent | null>(() => peekAdminPwaInstallPrompt());
  const [installOfferVisible, setInstallOfferVisible] = useState(() =>
    isAdminInstallOfferDue(Date.now(), window.localStorage, surface),
  );
  const [showIosGuide, setShowIosGuide] = useState(() =>
    readIosGuideEligible(surface),
  );
  const [updateReady, setUpdateReady] = useState(false);
  const [applyUpdate, setApplyUpdate] = useState<(() => void) | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshPausedOffline, setRefreshPausedOffline] = useState(false);
  const [showTerminalRecovery, setShowTerminalRecovery] = useState(false);
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const forceUpdateEnabled = readPwaForceUpdateEnabled();
  const updateShownEmittedRef = useRef(false);
  const refreshLockRef = useRef(false);
  const refreshWatchdogRef = useRef<number | null>(null);
  const refreshEscalationIndexRef = useRef(0);
  const refreshLadderCycleRef = useRef(0);
  const refreshEscalationInFlightRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const refreshPausedOfflineRef = useRef(false);
  const installOfferMarkedRef = useRef(false);
  const criticalFlowActive = isAdminCriticalFlowActive();

  useEffect(() => {
    stripAdminPwaRecoveryQueryFromUrl();
  }, []);

  useEffect(() => {
    stampAdminPwaInstallEvidenceIfStandalone();
  }, []);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  useEffect(() => {
    refreshPausedOfflineRef.current = refreshPausedOffline;
  }, [refreshPausedOffline]);

  const clearRefreshWatchdog = (): void => {
    if (refreshWatchdogRef.current === null) {
      return;
    }
    window.clearTimeout(refreshWatchdogRef.current);
    refreshWatchdogRef.current = null;
  };

  const endRefreshSession = useCallback((): void => {
    clearRefreshWatchdog();
    refreshLockRef.current = false;
    refreshEscalationInFlightRef.current = false;
    isRefreshingRef.current = false;
    setIsRefreshing(false);
    setRefreshPausedOffline(false);
    setShowTerminalRecovery(false);
    resumeDirtyLeaveAfterNavigation();
  }, []);

  const handleRefreshWatchdogTimeoutRef = useRef<(() => Promise<void>) | null>(null);

  const scheduleRefreshWatchdog = useCallback((): void => {
    if (refreshPausedOfflineRef.current) {
      return;
    }
    clearRefreshWatchdog();
    refreshWatchdogRef.current = window.setTimeout(() => {
      refreshWatchdogRef.current = null;
      void handleRefreshWatchdogTimeoutRef.current?.();
    }, ADMIN_PWA_SAFE_REFRESH_WATCHDOG_MS);
  }, []);

  const handleRefreshWatchdogTimeout = useCallback(async (): Promise<void> => {
    // Mutex: concurrent watchdog timeout + persisted pageshow must not double-advance
    // refreshEscalationIndexRef or fire parallel navigations.
    if (refreshEscalationInFlightRef.current) {
      return;
    }
    if (refreshPausedOfflineRef.current) {
      return;
    }

    refreshEscalationInFlightRef.current = true;

    try {
      const stepIndex = refreshEscalationIndexRef.current;
      const steps = ADMIN_PWA_REFRESH_ESCALATION_STEPS;
      const stepsPerCycle = steps.length;

      if (stepIndex > 0 && stepIndex % stepsPerCycle === 0) {
        refreshLadderCycleRef.current += 1;
      }

      if (refreshLadderCycleRef.current >= ADMIN_PWA_MAX_LADDER_CYCLES) {
        setShowTerminalRecovery(true);
        resumeDirtyLeaveAfterNavigation();
        clearRefreshWatchdog();
        return;
      }

      const rawStep =
        stepIndex < steps.length ? steps[stepIndex] : steps[steps.length - 1];
      const step: AdminPwaRefreshEscalationStep = rawStep ?? 'origin_replace';

      if (stepIndex === 0) {
        emitAdminPwaAnalytics({
          eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_APPLIED,
          metadata: { outcome: 'watchdog_timeout' },
        });
      }

      refreshEscalationIndexRef.current = stepIndex + 1;
      emitAdminPwaAnalytics({
        eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_APPLIED,
        metadata: { outcome: `escalation_${step}` },
      });

      try {
        await runLiveAdminPwaRefreshEscalationStep(step);
      } catch {
        // Next watchdog tick will retry with a stronger step.
      }

      scheduleRefreshWatchdog();
    } finally {
      refreshEscalationInFlightRef.current = false;
    }
  }, [scheduleRefreshWatchdog]);

  useLayoutEffect(() => {
    handleRefreshWatchdogTimeoutRef.current = handleRefreshWatchdogTimeout;
  });

  const resumeRefreshIfPaused = useCallback((): void => {
    if (!refreshLockRef.current && !isRefreshingRef.current) {
      return;
    }
    if (!refreshPausedOfflineRef.current) {
      if (refreshWatchdogRef.current === null) {
        scheduleRefreshWatchdog();
      }
      return;
    }
    setRefreshPausedOffline(false);
    refreshPausedOfflineRef.current = false;
    scheduleRefreshWatchdog();
    void handleRefreshWatchdogTimeoutRef.current?.();
  }, [scheduleRefreshWatchdog]);

  useEffect(() => {
    const onPageHide = (): void => {
      if (refreshWatchdogRef.current !== null) {
        window.clearTimeout(refreshWatchdogRef.current);
        refreshWatchdogRef.current = null;
      }
    };
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!refreshLockRef.current && !isRefreshingRef.current) {
        return;
      }
      if (event.persisted) {
        void handleRefreshWatchdogTimeoutRef.current?.();
        return;
      }
      resumeRefreshIfPaused();
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      resumeRefreshIfPaused();
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearRefreshWatchdog();
    };
  }, [resumeRefreshIfPaused]);

  useEffect(() => {
    return subscribeAdminPwaInstallPrompt(() => {
      setDeferredPrompt(peekAdminPwaInstallPrompt());
    });
  }, []);

  useEffect(() => {
    syncAdminPwaDocumentChrome(location.pathname);
    if (surfaceRef.current !== surface) {
      surfaceRef.current = surface;
      installOfferMarkedRef.current = false;
      setAdminPwaInstallPrompt(null);
      const due = isAdminInstallOfferDue(Date.now(), window.localStorage, surface);
      setInstallOfferVisible(due);
      setShowIosGuide(due && isIosSafari());
    }
  }, [location.pathname, surface]);

  useEffect(() => {
    const onBeforeInstall = (event: Event): void => {
      event.preventDefault();
      const current = resolveAdminPwaInstallSurface(window.location.pathname);
      setAdminPwaInstallPrompt(event as AdminBeforeInstallPromptEvent);
      setInstallOfferVisible(
        isAdminInstallOfferDue(Date.now(), window.localStorage, current),
      );
    };
    const onAppInstalled = (): void => {
      markAdminPwaInstallEvidence();
      setAdminPwaInstallPrompt(null);
      setInstallOfferVisible(false);
      setShowIosGuide(false);
      clearAdminInstallOfferDismissed(window.localStorage, surfaceRef.current);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  useEffect(() => {
    registerAdminPwaServiceWorker({
      setUpdateReady,
      setApplyUpdate,
    });
    return listenAdminPwaReloadChannel();
  }, []);

  /**
   * G6 deploy/version gate: poll uncached `/runtime-config.json` while visible.
   * Mismatch → nuclear force ladder (supersedes Apply-waiting banner when both apply).
   * See `adminDeployVersionGate.ts` header for Apply interaction.
   */
  useEffect(() => {
    return startAdminDeployVersionGatePoll();
  }, []);

  useEffect(() => {
    const onOnline = (): void => {
      setOffline(false);
      resumeRefreshIfPaused();
    };
    const onOffline = (): void => {
      setOffline(true);
      if (refreshLockRef.current || isRefreshingRef.current) {
        clearRefreshWatchdog();
        setRefreshPausedOffline(true);
        refreshPausedOfflineRef.current = true;
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [resumeRefreshIfPaused]);

  useEffect(() => {
    if (!updateReady || updateShownEmittedRef.current) {
      return;
    }
    updateShownEmittedRef.current = true;
    if (isAdminCriticalFlowActive()) {
      emitAdminPwaAnalytics({
        eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_DEFERRED,
        metadata: { outcome: 'critical_flow' },
      });
      return;
    }
    emitAdminPwaAnalytics({
      eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_SHOWN,
    });
  }, [updateReady]);

  const beginSafeRefresh = (options?: {
    readonly outcome?: string;
  }): void => {
    if (refreshLockRef.current || isRefreshing) {
      return;
    }
    if (isAdminCriticalFlowActive()) {
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      refreshLockRef.current = true;
      refreshEscalationIndexRef.current = 0;
      refreshLadderCycleRef.current = 0;
      setIsRefreshing(true);
      isRefreshingRef.current = true;
      setRefreshPausedOffline(true);
      refreshPausedOfflineRef.current = true;
      return;
    }

    refreshLockRef.current = true;
    refreshEscalationIndexRef.current = 0;
    refreshLadderCycleRef.current = 0;
    setShowTerminalRecovery(false);
    setRefreshPausedOffline(false);
    refreshPausedOfflineRef.current = false;
    setIsRefreshing(true);
    isRefreshingRef.current = true;
    suspendDirtyLeaveForNavigation();
    emitAdminPwaAnalytics({
      eventName: ANALYTICS_PWA_EVENTS.PWA_UPDATE_APPLIED,
      metadata: options?.outcome !== undefined ? { outcome: options.outcome } : undefined,
    });

    scheduleRefreshWatchdog();

    try {
      if (applyUpdate !== null) {
        applyUpdate();
        return;
      }
      window.location.reload();
    } catch {
      void handleRefreshWatchdogTimeout();
    }
  };

  const handleTerminalRecoveryRetry = (): void => {
    endRefreshSession();
    beginSafeRefresh({ outcome: 'terminal_recovery_retry' });
  };

  const showInstallOffer = deferredPrompt !== null && installOfferVisible;
  const showIosInstallGuide =
    showIosGuide && deferredPrompt === null && installOfferVisible;
  const installTitleKey =
    surface === 'dev' ? 'pwa.installTitleDev' : 'pwa.installTitle';
  const installPromptKey =
    surface === 'dev' ? 'pwa.installPromptDev' : 'pwa.installPrompt';

  useEffect(() => {
    if (!showInstallOffer && !showIosInstallGuide) {
      return;
    }
    if (installOfferMarkedRef.current) {
      return;
    }
    installOfferMarkedRef.current = true;
    markAdminInstallOfferShown(Date.now(), window.localStorage, surface);
  }, [showInstallOffer, showIosInstallGuide, surface]);

  const handleInstall = async (): Promise<void> => {
    if (isRefreshing) {
      return;
    }
    const promptEvent = takeAdminPwaInstallPrompt();
    if (promptEvent === null) {
      setInstallOfferVisible(false);
      return;
    }
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    emitAdminPwaAnalytics({
      eventName:
        choice.outcome === 'accepted'
          ? ANALYTICS_PWA_EVENTS.PWA_INSTALL_ACCEPTED
          : ANALYTICS_PWA_EVENTS.PWA_INSTALL_DISMISSED,
      metadata: { outcome: choice.outcome, pwaSurface: surface },
    });
    if (choice.outcome === 'accepted') {
      markAdminPwaInstallEvidence();
      clearAdminInstallOfferDismissed(window.localStorage, surface);
    } else {
      markAdminInstallOfferDismissed(Date.now(), window.localStorage, surface);
    }
    setInstallOfferVisible(false);
  };

  const handleDismissInstall = (): void => {
    if (isRefreshing) {
      return;
    }
    emitAdminPwaAnalytics({
      eventName: ANALYTICS_PWA_EVENTS.PWA_INSTALL_DISMISSED,
      metadata: { outcome: 'banner_cancel', pwaSurface: surface },
    });
    markAdminInstallOfferDismissed(Date.now(), window.localStorage, surface);
    setAdminPwaInstallPrompt(null);
    setInstallOfferVisible(false);
  };

  const handleApplyUpdate = (): void => {
    beginSafeRefresh();
  };

  const handleForceRefresh = (): void => {
    beginSafeRefresh({ outcome: 'force_refresh' });
  };

  const refreshOverlayLabel = refreshPausedOffline
    ? t('pwa.refreshOfflineDeferred')
    : t('pwa.refreshing');

  if (
    !offline &&
    !showInstallOffer &&
    !showIosInstallGuide &&
    !updateReady &&
    !forceUpdateEnabled &&
    !isRefreshing
  ) {
    return <AdminTestEconomyChrome />;
  }

  const applyDisabled = isRefreshing || criticalFlowActive;
  const showLifecycleChrome =
    offline ||
    showInstallOffer ||
    showIosInstallGuide ||
    updateReady ||
    forceUpdateEnabled;

  const lifecycleChrome = showLifecycleChrome ? (
    <div
      className={ADMIN_PWA_LIFECYCLE_CHROME_CLASSNAME}
      style={adminPwaLifecycleChromeStyle()}
      data-testid="admin-pwa-lifecycle"
      data-pwa-surface={surface}
      aria-hidden={isRefreshing || undefined}
    >
      {offline ? (
        <div className="pointer-events-auto">
          <OfflineBanner message={t('pwa.offlineBanner')} />
        </div>
      ) : null}
      {showInstallOffer ? (
        <Card
          padded
          className={ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME}
          role="region"
          aria-label={t(installTitleKey)}
        >
          <h2 className="text-base font-semibold text-[var(--color-an-text)]">
            {t(installTitleKey)}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-an-text-muted)]">
            {t(installPromptKey)}
          </p>
          <div className={ADMIN_PWA_LIFECYCLE_ACTIONS_CLASSNAME}>
            <Button
              type="button"
              intent="primary"
              size="md"
              className={ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME}
              onClick={() => void handleInstall()}
              disabled={isRefreshing}
              data-testid="admin-pwa-install-button"
            >
              {t('pwa.installAction')}
            </Button>
            <Button
              type="button"
              intent="secondary"
              size="md"
              className={ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME}
              onClick={handleDismissInstall}
              disabled={isRefreshing}
              data-testid="admin-pwa-install-dismiss"
            >
              {t('pwa.installDismiss')}
            </Button>
          </div>
        </Card>
      ) : null}
      {showIosInstallGuide ? (
        <Card
          padded
          className={ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME}
          role="region"
          aria-label={t(installTitleKey)}
          data-testid="admin-pwa-ios-guide"
        >
          <h2 className="text-base font-semibold text-[var(--color-an-text)]">
            {t(installTitleKey)}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-an-text-muted)]">
            {t('pwa.iosInstallGuide')}
          </p>
        </Card>
      ) : null}
      {updateReady ? (
        <Card
          padded
          className={ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME}
          role="region"
          aria-label={t('pwa.updateTitle')}
          data-testid="admin-pwa-update-offer"
        >
          <h2 className="text-base font-semibold text-[var(--color-an-text)]">
            {t('pwa.updateTitle')}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-an-text-muted)]">
            {t('pwa.updateReady')}
          </p>
          {criticalFlowActive ? (
            <p className="mt-2 text-xs text-[var(--color-an-text-muted)]">
              {t('pwa.updateDeferredCritical')}
            </p>
          ) : null}
          <div className={ADMIN_PWA_LIFECYCLE_ACTIONS_CLASSNAME}>
            <Button
              type="button"
              intent="primary"
              size="md"
              className={ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME}
              onClick={handleApplyUpdate}
              disabled={applyDisabled}
              loading={Boolean(isRefreshing && !refreshPausedOffline)}
              aria-disabled={applyDisabled || undefined}
              data-testid="admin-pwa-apply-update"
            >
              {t('pwa.updateAction')}
            </Button>
          </div>
        </Card>
      ) : null}
      {forceUpdateEnabled ? (
        <Card
          padded
          className={ADMIN_PWA_LIFECYCLE_CARD_CLASSNAME}
          role="region"
          aria-label={t('pwa.forceRefreshTitle')}
        >
          <h2 className="text-base font-semibold text-[var(--color-an-text)]">
            {t('pwa.forceRefreshTitle')}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-an-text-muted)]">
            {t('pwa.forceRefreshBody')}
          </p>
          <div className={ADMIN_PWA_LIFECYCLE_ACTIONS_CLASSNAME}>
            <Button
              type="button"
              intent="primary"
              size="md"
              className={ADMIN_PWA_LIFECYCLE_ACTION_BUTTON_CLASSNAME}
              onClick={handleForceRefresh}
              disabled={Boolean(isRefreshing && !refreshPausedOffline)}
              loading={Boolean(isRefreshing && !refreshPausedOffline)}
              data-testid="pwa-force-refresh"
            >
              {t('pwa.forceRefresh')}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <AdminTestEconomyChrome />
      {isRefreshing ? (
        <PwaRefreshBlockingOverlay
          label={refreshOverlayLabel}
          recoveryHint={
            showTerminalRecovery ? t('pwa.refreshRecoveryHint') : undefined
          }
          recoveryRetryLabel={
            showTerminalRecovery ? t('pwa.refreshRetry') : undefined
          }
          recoveryOpenLinkLabel={
            showTerminalRecovery ? t('pwa.refreshOpenApp') : undefined
          }
          recoveryOpenLinkHref={
            showTerminalRecovery ? buildAdminPwaCacheBustUrl() : undefined
          }
          onRecoveryRetry={
            showTerminalRecovery ? handleTerminalRecoveryRetry : undefined
          }
        />
      ) : null}
      {lifecycleChrome !== null && typeof document !== 'undefined'
        ? createPortal(lifecycleChrome, document.body)
        : null}
    </>
  );
}
